import React from 'react';

import { useMutation } from '@tanstack/react-query';

import { config } from '@/_config';
import { apiClient, snackbar } from '@/_services';

import type { LoginApiErrorResponse, LoginApiResponse, LoginHandlerParams } from './types';

interface IFBErrorResponse extends fb.StatusResponse {
  error: string;
}

interface IFBResponse {
  email: string;
  name: string;
  userId: string;
  accessToken: string;
  expiresIn: number;
}

type TFBCallback = (r: IFBResponse | IFBErrorResponse) => void;

export type FacebookLoginApiPayload = {
  token: string;
};

export const useFacebookLogin = ({ onSuccess, onError }: LoginHandlerParams) => {
  const [isLoaded, setLoaded] = React.useState(typeof window.FB !== 'undefined');
  const { mutateAsync: loginQuery, isPending: isLoading } = useMutation<
    LoginApiResponse,
    LoginApiErrorResponse,
    FacebookLoginApiPayload
  >({
    mutationFn: (payload) =>
      apiClient.post({
        endpoint: '/facebook/login/',
        payload,
        isAuth: false,
      }),
  });

  const load = React.useCallback((onLoad: () => void) => {
    window.fbAsyncInit = () => {
      window.FB.init({
        appId: config.facebookLoginAppId,
        cookie: true, // enable cookies to allow the server to access
        status: false,
        xfbml: true,
        version: 'v14.0',
      });
    };

    // Load the SDK asynchronously
    ((d, s, id) => {
      const fjs = d.getElementsByTagName(s)[0] as Element;
      if (d.getElementById(id)) {
        if (onLoad) onLoad();
        return;
      }

      const js = d.createElement(s);
      js.id = id;
      js.onload = () => {
        if (onLoad) onLoad();
      };
      js.setAttribute('src', '//connect.facebook.net/en_US/sdk.js');
      fjs.parentNode?.insertBefore(js, fjs);
    })(document, 'script', 'facebook-jssdk');
  }, []);

  const fetchData = (authRes: fb.StatusResponse, callback: TFBCallback) => {
    window.FB.api(
      '/me',
      {
        fields: 'id, name, email',
      },
      (response: { email: string; name: string }) => {
        const res = authRes.authResponse || {};
        callback({
          email: response.email,
          name: response.name,
          userId: res.userID,
          accessToken: res.accessToken || '',
          expiresIn: res.expiresIn,
        });
      }
    );
  };

  const login = (callback: TFBCallback) => {
    window.FB.login(
      (response) => {
        if (response.status === 'connected') {
          fetchData(response, callback);
        } else {
          // TODO fix
          // callback({ error: 'login error', ...response } as IFBErrorResponse);
        }
      },
      { scope: 'public_profile, email' }
    );
  };

  const gotResponse: TFBCallback = (res) => {
    const { accessToken } = res as IFBResponse;
    if (!accessToken) {
      snackbar.info("We couldn't gather information. Please contact Support team.");
    } else {
      loginQuery({ token: accessToken }, { onSuccess, onError });
    }
  };

  const initLogin = () => {
    window.FB.getLoginStatus((response) => {
      if (response.status === 'connected') {
        fetchData(response, gotResponse);
      } else {
        login(gotResponse);
      }
    });
  };

  const logout = (callback: () => void) => {
    load(() => {
      window.FB.getLoginStatus((response) => {
        if (!response.authResponse) {
          if (callback) callback();
        } else {
          window.FB.logout(() => {
            if (callback) callback();
          });
        }
      });
    });
  };

  React.useEffect(() => {
    if (!isLoaded)
      load(() => {
        setLoaded(true);
      });
  }, [load, isLoaded]);

  return { isLoaded, isLoading, initLogin, logout };
};
